/**
 * LLM API endpoint detection + response body parsing.
 *
 * Used by the fetch interceptor to recognize LLM provider calls and extract
 * usage metadata (model, input/output token counts) from response bodies.
 *
 * The body is read in-process inside the customer's harness. Prompt and
 * completion content (`messages`, `content`, `choices[].message.content`)
 * never crosses the wire to AgentVault — only the extracted metadata.
 */

export type LlmProvider =
  | "anthropic"
  | "openai"
  | "openrouter"
  | "groq"
  | "together"
  | "mistral"
  | "deepseek"
  | "azure";

export interface ParsedLlmResponse {
  model: string;
  tokensInput: number;
  tokensOutput: number;
}

/**
 * Detect whether a URL is a known LLM API endpoint we can parse.
 * Returns the provider key, or null if not recognized.
 *
 * Coverage as of 2026-05-01: Anthropic + 7 OpenAI-compatible providers.
 * Bedrock and Gemini intentionally NOT included — they require dedicated
 * parser branches (region routing for Bedrock, different usage shape for
 * Gemini). Add via separate PR when needed.
 */
export function detectLlmEndpoint(url: string): LlmProvider | null {
  if (!url) return null;

  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return null;
  }

  const host = parsed.hostname.toLowerCase();
  const path = parsed.pathname;

  // Anthropic public API
  if (host === "api.anthropic.com" && path === "/v1/messages") {
    return "anthropic";
  }

  // OpenAI public API: chat completions + responses API
  if (host === "api.openai.com" && (path === "/v1/chat/completions" || path === "/v1/responses")) {
    return "openai";
  }

  // OpenRouter — multi-provider router with OpenAI-compatible shape.
  // Returns model as `provider/name` (e.g., "openai/gpt-4o-mini") which
  // we normalize in the parser so the pricing table can match.
  if (host === "openrouter.ai" && path === "/api/v1/chat/completions") {
    return "openrouter";
  }

  // Groq — OpenAI-compat. Note the unusual /openai/v1/ path prefix.
  if (host === "api.groq.com" && path === "/openai/v1/chat/completions") {
    return "groq";
  }

  // Together AI — OpenAI-compat
  if (host === "api.together.xyz" && path === "/v1/chat/completions") {
    return "together";
  }

  // Mistral — OpenAI-compat
  if (host === "api.mistral.ai" && path === "/v1/chat/completions") {
    return "mistral";
  }

  // DeepSeek — OpenAI-compat
  if (host === "api.deepseek.com" && path === "/v1/chat/completions") {
    return "deepseek";
  }

  // Azure OpenAI — any deployment subdomain. Strict suffix check (no
  // arbitrary subdomain spoofing) and path must include /openai/deployments/.
  if (
    host.endsWith(".openai.azure.com") &&
    path.includes("/openai/deployments/") &&
    path.endsWith("/chat/completions")
  ) {
    return "azure";
  }

  return null;
}

function isPlainObject(x: unknown): x is Record<string, unknown> {
  return typeof x === "object" && x !== null && !Array.isArray(x);
}

function asNumber(x: unknown): number | null {
  return typeof x === "number" && Number.isFinite(x) ? x : null;
}

/**
 * Parse an LLM API response body, returning normalized usage metadata.
 *
 * Returns null if the body is not parseable or usage fields are missing.
 * Treats a missing `model` as `"unknown"` to avoid losing token counts when
 * a provider response shape varies. Does NOT extract prompt/completion
 * content — that is irrelevant to billing/observability and would violate
 * the plaintext constraint if transmitted.
 */
export function parseLlmResponse(
  provider: LlmProvider,
  body: unknown,
): ParsedLlmResponse | null {
  if (!isPlainObject(body)) return null;

  if (provider === "anthropic") return parseAnthropic(body);

  // All remaining providers use the OpenAI-compatible response shape.
  const result = parseOpenAI(body);
  if (!result) return null;

  // OpenRouter returns model as `provider/name` (e.g., "openai/gpt-4o-mini").
  // Strip the provider prefix so the pricing table's longest-prefix match
  // can find the canonical model name.
  if (provider === "openrouter") {
    const slash = result.model.indexOf("/");
    if (slash !== -1) {
      result.model = result.model.slice(slash + 1);
    }
  }

  return result;
}

function parseAnthropic(body: Record<string, unknown>): ParsedLlmResponse | null {
  if (!isPlainObject(body.usage)) return null;
  const usage = body.usage;

  const inputBase = asNumber(usage.input_tokens);
  const cacheCreate = asNumber(usage.cache_creation_input_tokens) ?? 0;
  const cacheRead = asNumber(usage.cache_read_input_tokens) ?? 0;
  const output = asNumber(usage.output_tokens);

  if (inputBase === null || output === null) return null;

  const model = typeof body.model === "string" ? body.model : "unknown";

  return {
    model,
    tokensInput: inputBase + cacheCreate + cacheRead,
    tokensOutput: output,
  };
}

function parseOpenAI(body: Record<string, unknown>): ParsedLlmResponse | null {
  if (!isPlainObject(body.usage)) return null;
  const usage = body.usage;

  // Chat Completions API uses prompt_tokens / completion_tokens.
  // Responses API uses input_tokens / output_tokens.
  const input = asNumber(usage.prompt_tokens) ?? asNumber(usage.input_tokens);
  const output = asNumber(usage.completion_tokens) ?? asNumber(usage.output_tokens);

  if (input === null || output === null) return null;

  const model = typeof body.model === "string" ? body.model : "unknown";

  return {
    model,
    tokensInput: input,
    tokensOutput: output,
  };
}

/**
 * Parse a Server-Sent Events (SSE) stream body and extract usage metadata.
 *
 * Modern LLM SDKs (Anthropic SDK, OpenAI SDK, OpenRouter via OpenAI SDK)
 * default to streaming, returning `Content-Type: text/event-stream` rather
 * than `application/json`. The body is a sequence of `data: {json}\n\n`
 * blocks; usage is reported in a terminal event.
 *
 * - Anthropic emits `message_start` (input tokens) and `message_delta`
 *   (cumulative output tokens) events. The final `message_delta` carries
 *   the authoritative output_tokens count.
 * - OpenAI/OpenRouter only include a `usage` field in the final chunk
 *   (just before `data: [DONE]`) when the harness sets
 *   `stream_options.include_usage = true`. If not requested, this returns
 *   null cleanly — no false data shipped.
 *
 * Returns null on empty/malformed streams or when usage is not present.
 */
export function parseLlmSseStream(
  provider: LlmProvider,
  text: string,
): ParsedLlmResponse | null {
  if (typeof text !== "string" || text.length === 0) return null;

  const payloads = extractDataPayloads(text);
  if (payloads.length === 0) return null;

  if (provider === "anthropic") return parseAnthropicSse(payloads);

  // All remaining providers use OpenAI-compatible streaming format.
  const result = parseOpenAiSse(payloads);
  if (!result) return null;

  if (provider === "openrouter") {
    const slash = result.model.indexOf("/");
    if (slash !== -1) {
      result.model = result.model.slice(slash + 1);
    }
  }

  return result;
}

/**
 * Walk an SSE body, returning the parsed JSON of every `data:` payload
 * in order. Skips `[DONE]` markers and silently drops malformed JSON.
 * Handles both LF and CRLF line endings.
 */
function extractDataPayloads(text: string): Record<string, unknown>[] {
  const normalized = text.replace(/\r\n/g, "\n");
  const blocks = normalized.split(/\n\n+/);
  const out: Record<string, unknown>[] = [];

  for (const block of blocks) {
    const dataLines: string[] = [];
    for (const rawLine of block.split("\n")) {
      const line = rawLine.trimEnd();
      if (line.startsWith("data:")) {
        // SSE allows "data: foo" or "data:foo"
        dataLines.push(line.slice(5).replace(/^ /, ""));
      }
    }
    if (dataLines.length === 0) continue;
    const joined = dataLines.join("\n");
    if (joined === "[DONE]") continue;
    try {
      const parsed = JSON.parse(joined);
      if (isPlainObject(parsed)) out.push(parsed);
    } catch {
      // Malformed JSON in a data: line — skip this block, keep going.
    }
  }

  return out;
}

function parseAnthropicSse(
  payloads: Record<string, unknown>[],
): ParsedLlmResponse | null {
  let model = "unknown";
  let inputBase: number | null = null;
  let cacheCreate = 0;
  let cacheRead = 0;
  let outputTokens: number | null = null;

  for (const p of payloads) {
    const type = p.type;

    if (type === "message_start" && isPlainObject(p.message)) {
      const msg = p.message;
      if (typeof msg.model === "string") model = msg.model;
      if (isPlainObject(msg.usage)) {
        const usage = msg.usage;
        inputBase = asNumber(usage.input_tokens);
        cacheCreate = asNumber(usage.cache_creation_input_tokens) ?? 0;
        cacheRead = asNumber(usage.cache_read_input_tokens) ?? 0;
        // message_start.usage.output_tokens is a placeholder (typically 1);
        // message_delta will overwrite it with the cumulative final count.
        const initialOutput = asNumber(usage.output_tokens);
        if (initialOutput !== null) outputTokens = initialOutput;
      }
    } else if (type === "message_delta" && isPlainObject(p.usage)) {
      const out = asNumber(p.usage.output_tokens);
      if (out !== null) outputTokens = out;
    }
  }

  if (inputBase === null || outputTokens === null) return null;

  return {
    model,
    tokensInput: inputBase + cacheCreate + cacheRead,
    tokensOutput: outputTokens,
  };
}

function parseOpenAiSse(
  payloads: Record<string, unknown>[],
): ParsedLlmResponse | null {
  // Find the latest payload that carries a usable `usage` field.
  // Walking backwards short-circuits to the terminal usage chunk that
  // OpenAI/OpenRouter emit when stream_options.include_usage = true.
  for (let i = payloads.length - 1; i >= 0; i--) {
    const p = payloads[i];
    if (!isPlainObject(p.usage)) continue;
    const usage = p.usage;
    const input = asNumber(usage.prompt_tokens) ?? asNumber(usage.input_tokens);
    const output =
      asNumber(usage.completion_tokens) ?? asNumber(usage.output_tokens);
    if (input === null || output === null) continue;
    const model = typeof p.model === "string" ? p.model : "unknown";
    return { model, tokensInput: input, tokensOutput: output };
  }
  return null;
}
